import { ReactNode } from "react";

export const CARD_CLASS = "bg-white rounded-xl border border-gray-200/80 shadow-sm";

export const PageHeader = ({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) => (
  <div className={`${CARD_CLASS} px-4 sm:px-6 py-4`}>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-base font-semibold leading-6 text-gray-900">{title}</h1>
        {subtitle && <div className="mt-1 text-xs text-gray-500">{subtitle}</div>}
      </div>
      {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-3">{actions}</div>}
    </div>
    {children}
  </div>
);

// One white card per form section, like the coupon setup sections.
export const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <div className={`${CARD_CLASS} px-4 sm:px-6 py-5`}>
    <div className="mb-3 border-b border-gray-100 pb-3">
      <h3 className="text-sm font-bold uppercase tracking-wide text-gray-800">{title}</h3>
      {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
    </div>
    {children}
  </div>
);

// Label-left / field-right row, as on the other Inventory Setup forms.
export const FormRow = ({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5 py-2 sm:flex-row sm:items-start sm:gap-0">
    <div className="w-full sm:w-3/12 sm:pt-2 lg:w-2/12">
      <p className="text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-400">*</span>}
      </p>
    </div>
    <div className="w-full sm:w-9/12 lg:w-7/12">
      {children}
      {hint && <p className="mt-1 text-[11px] text-gray-500">{hint}</p>}
    </div>
  </div>
);

// Small labelled field used when several fields share one row.
export const InlineField = ({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1">
    <span className="text-xs font-medium text-gray-600">
      {label} {required && <span className="text-red-400">*</span>}
    </span>
    {children}
  </div>
);

// Grey inset for conditional fields (coupon applicability style).
export const SubPanel = ({ children }: { children: ReactNode }) => (
  <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50/50 p-4">{children}</div>
);

// "A, B +3 more" with the full list on hover.
export const TruncatedList = ({ names, max = 2 }: { names: string[]; max?: number }) => {
  if (!names.length) return <span className="text-gray-400">—</span>;
  const shown = names.slice(0, max).join(", ");
  const rest = names.length - max;
  return (
    <span title={names.join(", ")}>
      {shown}
      {rest > 0 && <span className="ml-1 font-semibold text-[#0068FF]">+{rest} more</span>}
    </span>
  );
};

// MultiSelectDropdown caps + centres itself (max-w-2xl mx-auto); make it fill the row like the other selects.
export const FULL_WIDTH_DROPDOWN = "w-full !max-w-none !mx-0";
